import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

// =============================================================================
// Reusable manual-quote-item library (svc-04, /v1/manual-quote-items*).
// Mirrors packages/shared-types/src/svc-04-admin-config/v1/manual-quote-items.schema.ts
// and the web-admin client (apps/web-admin/src/api/manualQuoteItems.ts).
//
// This is the POC "Reusable library" pane shown inside the customer Configure
// modal. The list (GET) is JWT-only, so any quote-capable staff member can read
// + click-to-fill. Writes (POST/PATCH) require `canManageMasterData` (admin) —
// the modal only surfaces the "save to library" checkbox for admins.
//
// Money is a Decimal-precision string on the wire (rule §D.12); `id` is a
// server-owned UUID (never sent on create).
// =============================================================================

export interface ManualQuoteItem {
  id: string;
  isManual: boolean | null;
  label: string | null;
  partNo: string | null;
  description: string | null;
  category: string | null;
  toolingType: string | null;
  machineType: string | null;
  itemName: string | null;
  partName: string | null;
  currency: string | null;
  price: string | null;
  qty: number | null;
}

export interface ManualQuoteItemList {
  data: ManualQuoteItem[];
  total: number;
}

/** POST body — `id` is NEVER sent (server-generated). */
export interface CreateManualQuoteItemBody {
  isManual?: boolean;
  label?: string | null;
  partNo?: string | null;
  description: string;
  category?: string | null;
  toolingType?: string | null;
  machineType?: string | null;
  itemName?: string | null;
  partName?: string | null;
  currency: string;
  /** Decimal string, up to 4 dp. */
  price: string;
  qty: number;
}

export type UpdateManualQuoteItemBody = Partial<CreateManualQuoteItemBody>;

const BASE = "/v1/manual-quote-items";

/** GET /v1/manual-quote-items — all active records (JWT-only). */
export async function listManualQuoteItems(): Promise<ManualQuoteItem[]> {
  const res = await Api.get(BASE);
  return unwrap<ManualQuoteItemList>(res.data).data;
}

/** POST /v1/manual-quote-items (admin / canManageMasterData). */
export async function createManualQuoteItem(
  body: CreateManualQuoteItemBody,
): Promise<ManualQuoteItem> {
  const res = await Api.post(BASE, body);
  return unwrap<ManualQuoteItem>(res.data);
}

/** PATCH /v1/manual-quote-items/:id (admin / canManageMasterData). */
export async function updateManualQuoteItem(
  id: string,
  body: UpdateManualQuoteItemBody,
): Promise<ManualQuoteItem> {
  const res = await Api.patch(`${BASE}/${encodeURIComponent(id)}`, body);
  return unwrap<ManualQuoteItem>(res.data);
}
